import { motion, AnimatePresence } from 'motion/react';
import {
  X,
  Bell,
  Heart,
  Upload,
  Trash2,
  Camera,
  Video,
  User,
  Sparkles,
  Check,
  CheckCheck,
} from 'lucide-react';
import { useNotificationsStore, type NotificationType } from '@/stores/notifications-store';
import { formatDistanceToNow } from '@/lib/utils';

const notificationConfig: Record<
  NotificationType,
  { icon: React.ElementType; color: string; bg: string }
> = {
  favorite_added: { icon: Heart, color: 'text-pink-400', bg: 'bg-pink-500/15' },
  favorite_removed: { icon: Heart, color: 'text-white/50', bg: 'bg-white/10' },
  model_uploaded: { icon: Upload, color: 'text-accent-blue', bg: 'bg-accent-blue/15' },
  model_deleted: { icon: Trash2, color: 'text-red-400', bg: 'bg-red-500/15' },
  capture_saved: { icon: Camera, color: 'text-green-400', bg: 'bg-green-500/15' },
  capture_deleted: { icon: Trash2, color: 'text-red-400', bg: 'bg-red-500/15' },
  profile_updated: { icon: User, color: 'text-accent-purple', bg: 'bg-accent-purple/15' },
  welcome: { icon: Sparkles, color: 'text-yellow-400', bg: 'bg-yellow-500/15' },
  ar_session: { icon: Video, color: 'text-accent-blue', bg: 'bg-accent-blue/15' },
  system: { icon: Bell, color: 'text-white/70', bg: 'bg-white/10' },
};

export default function NotificationsDrawer() {
  const {
    notifications,
    unreadCount,
    isDrawerOpen,
    setDrawerOpen,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
  } = useNotificationsStore();

  if (!isDrawerOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100]"
    >
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => setDrawerOpen(false)}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />

      {/* Drawer */}
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="absolute bottom-0 left-0 right-0 max-h-[85vh] bg-background rounded-t-[32px] overflow-hidden flex flex-col"
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pb-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-accent-blue/15 flex items-center justify-center">
              <Bell size={20} className="text-accent-blue" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Notifications</h2>
              {unreadCount > 0 && (
                <p className="text-sm text-white/50">{unreadCount} unread</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {notifications.length > 0 && (
              <>
                <button
                  onClick={markAllAsRead}
                  className="p-2.5 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                  title="Mark all as read"
                >
                  <CheckCheck size={18} className="text-white/60" />
                </button>
                <button
                  onClick={clearAll}
                  className="p-2.5 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                  title="Clear all"
                >
                  <Trash2 size={18} className="text-white/60" />
                </button>
              </>
            )}
            <button
              onClick={() => setDrawerOpen(false)}
              className="p-2.5 rounded-full bg-white/5 hover:bg-white/10 transition-colors ml-1"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto no-scrollbar">
          {notifications.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center justify-center py-16 px-6"
            >
              <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mb-4">
                <Bell size={32} className="text-white/30" />
              </div>
              <h3 className="font-bold text-lg mb-1">No notifications yet</h3>
              <p className="text-white/50 text-center text-sm">
                When you interact with models, your activity will appear here.
              </p>
            </motion.div>
          ) : (
            <div className="py-2">
              <AnimatePresence>
                {notifications.map((notification, index) => {
                  const config = notificationConfig[notification.type];
                  const Icon = config.icon;

                  return (
                    <motion.div
                      key={notification.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ delay: index * 0.03 }}
                      onClick={() => markAsRead(notification.id)}
                      className={`relative px-6 py-4 flex gap-4 cursor-pointer hover:bg-white/[0.03] transition-colors ${
                        !notification.read ? 'bg-white/[0.02]' : ''
                      }`}
                    >
                      {/* Unread indicator */}
                      {!notification.read && (
                        <div className="absolute left-2 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-accent-blue shadow-[0_0_8px_rgba(0,212,255,0.8)]" />
                      )}

                      {/* Icon */}
                      <div
                        className={`w-11 h-11 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0`}
                      >
                        {notification.thumbnailUrl ? (
                          <img
                            src={notification.thumbnailUrl}
                            alt=""
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          <Icon size={20} className={config.color} />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className={`font-semibold text-sm ${!notification.read ? 'text-white' : 'text-white/80'}`}>
                            {notification.title}
                          </h4>
                          <span className="text-xs text-white/40 flex-shrink-0">
                            {formatDistanceToNow(new Date(notification.createdAt))}
                          </span>
                        </div>
                        <p className="text-sm text-white/50 mt-0.5 line-clamp-2">
                          {notification.message}
                        </p>
                      </div>

                      {/* Actions */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notification.id);
                        }}
                        className="p-2 rounded-full hover:bg-white/10 transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100"
                      >
                        <X size={16} className="text-white/40" />
                      </button>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Safe Area Bottom */}
        <div className="safe-area-bottom" />
      </motion.div>
    </motion.div>
  );
}
